import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@zg-rentals/react-query5';
import { formatDistanceToNow } from 'date-fns';

import { Flex, mediaBreakpointMixin, Paragraph, Table } from '@zillow/constellation';
import { Menu, MenuItem, MenuPopper } from '@zillow/constellation';

import {
  numberUtils,
  RentalManagerUserMaintenanceTaskWebApiHandler_list,
  useIsScreenLG_GTE,
  useIsScreenSM_GTE,
  useIsScreenXL_GTE,
  useIsScreenXXL,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';

import { TASKS_TABLE, USE_TASKS_LIST } from '../constants/TaskConstants';
import { DeleteTaskModal } from './DeleteTaskModal';
import { StatusTag } from './StatusTag';

import { StyledAnchor, StyledRow } from '../StyledComponents';
import { TasksTableSkeleton } from './TasksTableSkeleton';

import type { RentalManagerUserMaintenanceTaskDto } from '@zg-rentals/rental-manager-env';

export const StyledOverflowParagraph = styled(Paragraph)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 150px;

  @media ${mediaBreakpointMixin('lg')} {
    width: 200px;
  }
`;

type Props = {
  listingAlias?: string;
  showAddressColumn?: boolean;
  fromExternalApp?: boolean;
  tasksToDisplay?: Array<RentalManagerUserMaintenanceTaskDto>;
  onRowClick?: (taskId: string) => void;
  isLoading?: boolean;
};

export const TasksTable = ({
  listingAlias,
  showAddressColumn = true,
  fromExternalApp = false,
  tasksToDisplay,
  onRowClick,
  isLoading = false,
}: Props) => {
  const history = useHistory();
  const isScreenLG_GTE = useIsScreenLG_GTE();
  const isScreenXL_GTE = useIsScreenXL_GTE();
  const isScreenSM_GTE = useIsScreenSM_GTE();
  const isScreenXXL = useIsScreenXXL();
  const paths = useRmwPaths();

  const tableRef = useRef<HTMLTableElement>(null);

  const [maintenanceTaskIdToDelete, setMaintenanceTaskIdToDelete] = useState('');
  const [idOfMenuPopper, setIdOfMenuPopper] = useState('');

  const { data, isInitialLoading } = useQuery({
    queryKey: [USE_TASKS_LIST],
    queryFn: () =>
      RentalManagerUserMaintenanceTaskWebApiHandler_list({
        includeInProgress: true,
      }),
    staleTime: 1000 * 3,
    enabled: tasksToDisplay && tasksToDisplay.length === 0,
  });

  const isDataLoading = isLoading || isInitialLoading;

  const handleTaskClick = (taskId: string) => {
    if (taskId) {
      if (fromExternalApp && onRowClick) {
        onRowClick(taskId);
      } else {
        history.push(`/rental-manager/maintenance-tasks/task?id=${taskId}`);
      }
    }
  };

  useEffect(() => {
    const closeOpenMenus = (e: MouseEvent) => {
      if (tableRef.current && idOfMenuPopper && !tableRef.current.contains(e.target as Node)) {
        setTimeout(() => {
          setIdOfMenuPopper('');
        }, 0);
      }
    };

    document.addEventListener('mouseup', closeOpenMenus);

    return () => document.removeEventListener('mouseup', closeOpenMenus);
  }, [idOfMenuPopper]);

  const tasksToRender = tasksToDisplay || data?.tasks;

  return (
    <>
      <Table appearance="horizontal" aria-label="Medium-sized horizontal table" size="md" ref={tableRef}>
        {isDataLoading ? (
          <TasksTableSkeleton />
        ) : (
          <>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                {isScreenLG_GTE && showAddressColumn && <Table.HeaderCell>Property address</Table.HeaderCell>}
                {isScreenSM_GTE && <Table.HeaderCell>Status</Table.HeaderCell>}

                {isScreenXL_GTE && <Table.HeaderCell>Last updated</Table.HeaderCell>}
                {isScreenXXL && <Table.HeaderCell>Cost</Table.HeaderCell>}
                <Table.HeaderCell></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tasksToRender?.map((task, idx) => {
                return (
                  <StyledRow
                    key={`${task.listingAlias}-${idx}`}
                    onClick={() => handleTaskClick(task.maintenanceTaskId)}
                    interactive={true}
                  >
                    <Table.Cell>
                      <StyledOverflowParagraph fontType="bodySmall">{task.name}</StyledOverflowParagraph>

                      {!isScreenLG_GTE && showAddressColumn && (
                        <StyledOverflowParagraph fontType="finePrint" fontColor="textMedium">
                          {task.fullAddress}
                        </StyledOverflowParagraph>
                      )}
                    </Table.Cell>
                    {isScreenLG_GTE && showAddressColumn && (
                      <Table.Cell>
                        <StyledOverflowParagraph fontType="bodySmall">{task.fullAddress}</StyledOverflowParagraph>
                      </Table.Cell>
                    )}
                    {isScreenSM_GTE && (
                      <Table.Cell>
                        <Flex display="flex" alignItems="center">
                          <StatusTag status={task.status} isLoading={isDataLoading} />
                        </Flex>
                      </Table.Cell>
                    )}
                    {isScreenXL_GTE && (
                      <Table.Cell>
                        {formatDistanceToNow(new Date(Number(task?.updatedTimestamp)), { addSuffix: true })}
                      </Table.Cell>
                    )}
                    {isScreenXXL && <Table.Cell>{numberUtils.asCurrencyWithDefault(task.cost / 100)}</Table.Cell>}

                    <Table.Cell>
                      <Flex
                        display="flex"
                        justifyContent="center"
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                        }}
                      >
                        <MenuPopper
                          isOpen={idOfMenuPopper === task.maintenanceTaskId}
                          onOpen={() => setIdOfMenuPopper(task.maintenanceTaskId)}
                          triggered={
                            <Menu>
                              {fromExternalApp ? (
                                <MenuItem>
                                  <StyledAnchor
                                    href={`${paths.maintenanceTrackerPage}/task?id=${task.maintenanceTaskId}`}
                                  >
                                    Edit task
                                  </StyledAnchor>
                                </MenuItem>
                              ) : (
                                <MenuItem
                                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    handleTaskClick(task.maintenanceTaskId);
                                  }}
                                >
                                  Edit task
                                </MenuItem>
                              )}
                              <MenuItem
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();

                                  setMaintenanceTaskIdToDelete(task.maintenanceTaskId);
                                }}
                              >
                                Delete task
                              </MenuItem>
                            </Menu>
                          }
                        />
                      </Flex>
                    </Table.Cell>
                  </StyledRow>
                );
              })}
            </Table.Body>
          </>
        )}
      </Table>
      {maintenanceTaskIdToDelete && (
        <DeleteTaskModal
          listingAlias={listingAlias}
          fromExternalApp={fromExternalApp}
          clickOriginationPoint={TASKS_TABLE}
          maintenanceTaskId={maintenanceTaskIdToDelete}
          onClose={() => {
            setMaintenanceTaskIdToDelete('');
          }}
        />
      )}
    </>
  );
};
